import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Linkedin, Github, MapPin } from 'lucide-react';
import { XLogo } from './icons/XLogo';
import { CONSULTATION_15MIN_URL } from '../lib/links';
import { goHome } from '../lib/goHome';
import { FooterNewsletter } from './FooterNewsletter';

/**
 * Footer — three sections stacked:
 *   1. FooterNewsletter — Field Notes signup with honeypot
 *   2. 6-column nav grid — Services / Industries / Compare / Company /
 *      Service Areas / Legal
 *   3. Bottom row — brand mark + 15-min booking CTA + copyright
 *
 * RULE — every public marketing page must be linked from this footer.
 * The grid is the canonical internal-linking surface that crawlers
 * (Google + AEO/GEO + LLM scrapers) walk on every cold load. Pulling
 * a page out of the footer kills its discoverability + drops PageRank
 * flow. See CLAUDE.md → "Footer rule" for the full routing convention.
 *
 * COLLAPSE PATTERN: Each column shows the first VISIBLE_PER_COLUMN
 * items (currently 5). Overflow links are rendered in the DOM but
 * given the HTML `hidden` attribute when collapsed — Googlebot fully
 * indexes them (Google has explicitly supported indexing display:none
 * / hidden content since 2016, reaffirmed by John Mueller multiple
 * times), and a "Show all N" button reveals them on click for users.
 * Triple-redundant discovery for AEO/GEO scrapers: links are in the
 * footer DOM + listed in /sitemap.xml + listed in /sitemap HTML page.
 *
 * Column ordering inside each list matters — first 5 items are the
 * always-visible high-equity links, remaining items are the
 * collapsible overflow. Reorder the source array, not the rendering
 * logic, when shifting priority.
 *
 * Columns and what lives where:
 *   - Services       → /web-design, /app-development,
 *                       /intelligent-agents, /operations-consulting,
 *                       and the /ai-agency umbrella page.
 *   - Industries     → vertical landing pages.
 *   - Compare        → every /compare/* page.
 *   - Company        → /about, /studio, /blog, /mythoughts, /bsf, /bsf/blog, /sitemap.
 *   - Service Areas  → every /ai-agency-{geo} local-SEO page.
 *   - Legal          → /privacy, /terms, /cookies, /unsubscribe.
 */

const VISIBLE_PER_COLUMN = 5;

interface FooterLink {
  label: string;
  to: string;
}

const SERVICES: FooterLink[] = [
  { label: 'AuraOS', to: '/auraos' },
  { label: 'AI Agents', to: '/intelligent-agents' },
  { label: 'Web Design', to: '/web-design' },
  { label: 'App Development', to: '/app-development' },
  { label: 'Operations Consulting', to: '/operations-consulting' },
  // AI Agency is the umbrella overview — kept past the visible-5 cutoff so
  // the Services column shows 5 like the others and this sits in "Show all".
  { label: 'AI Agency', to: '/ai-agency' },
];

// First 5 = always visible (highest organic-search volume verticals
// to protect link equity). Remaining items collapse behind the
// "Show all" toggle but stay in the DOM for crawlers.
const INDUSTRIES: FooterLink[] = [
  { label: 'Home Improvement', to: '/home-improvement' },
  { label: 'Real Estate', to: '/real-estate' },
  { label: 'Law Firms', to: '/law-firms' },
  { label: 'Medical & Dental', to: '/medical-dental' },
  { label: 'Restaurants', to: '/restaurants-hospitality' },
  // ─── collapsed below ───
  { label: 'Professional Services', to: '/professional-services' },
  { label: 'E-commerce', to: '/ecommerce' },
  { label: 'Auto Dealerships', to: '/auto-dealerships' },
  { label: 'Auto Repair', to: '/auto-repair' },
  { label: 'Auto Detailing', to: '/auto-detailing' },
  { label: 'Lawn Care & Landscaping', to: '/lawn-care-landscaping' },
  { label: 'Pool Building & Services', to: '/pool-building-services' },
  { label: 'Accounting & CPA', to: '/accounting-cpa' },
  { label: 'Fitness & Wellness', to: '/fitness-wellness' },
  { label: 'Marine & Boating', to: '/marine-boating' },
];

// First 5 = always visible (highest-volume competitor compares).
const COMPARE: FooterLink[] = [
  { label: 'vs. HubSpot', to: '/compare/brandcentral-crm-vs-hubspot' },
  { label: 'vs. Salesforce', to: '/compare/brandcentral-crm-vs-salesforce' },
  { label: 'vs. Monday.com', to: '/compare/brandcentral-crm-vs-monday' },
  { label: 'vs. Hiring In-House', to: '/compare/brandcentral-vs-hiring-in-house' },
  { label: 'vs. Webflow Studio', to: '/compare/brandcentral-vs-webflow-studio' },
  // ─── collapsed below ───
  { label: 'vs. Pipedrive', to: '/compare/brandcentral-crm-vs-pipedrive' },
  { label: 'vs. ClickUp', to: '/compare/brandcentral-crm-vs-clickup' },
  { label: 'vs. Notion', to: '/compare/brandcentral-crm-vs-notion' },
  { label: 'vs. Zoho CRM', to: '/compare/brandcentral-crm-vs-zoho' },
];

const COMPANY: FooterLink[] = [
  { label: 'About', to: '/about' },
  { label: 'Studio', to: '/studio' },
  { label: 'Field Notes (Blog)', to: '/blog' },
  { label: 'My Thoughts', to: '/mythoughts' },
  { label: 'Tools', to: '/tools' },
  { label: 'Sitemap', to: '/sitemap' },
  { label: 'Build SMARTER Faster', to: '/bsf' },
  { label: 'BSF Blog', to: '/bsf/blog' },
];

// Learn column — split out of Company per the CLAUDE.md overflow rule once
// the content surfaces (glossary, guides, report, tools, resources) pushed
// it past 9 items. Highest-equity AEO surfaces stay in the always-visible
// first 5; the hub indexes collapse behind "Show all" (SEO-safe — still in
// the DOM for crawlers).
const LEARN: FooterLink[] = [
  { label: 'AI Glossary', to: '/learn' },
  { label: 'AI Agents Guide', to: '/guides/ai-agents-for-service-businesses' },
  { label: 'ROI Calculator', to: '/tools/roi-calculator' },
  { label: 'Resources', to: '/resources' },
  { label: '2026 AI Report', to: '/reports/state-of-ai-service-businesses-2026' },
  // ─── collapsed below ─── (Tools lives in the Company column)
  { label: 'All Guides', to: '/guides' },
];

// States column (15 statewide /ai-agency-{state} pages). Split out from
// Service Areas once P11 pushed the combined list past the ~9-item cap
// (CLAUDE.md footer overflow rule). NC (home) first with neighboring
// South Carolina pinned directly beneath it, then the largest markets
// fill out the always-visible first 5; the rest collapse into "Show all"
// — still fully crawled, just not always visible.
const STATES: FooterLink[] = [
  { label: 'North Carolina', to: '/ai-agency-north-carolina' },
  { label: 'South Carolina', to: '/ai-agency-south-carolina' },
  { label: 'California', to: '/ai-agency-california' },
  { label: 'Texas', to: '/ai-agency-texas' },
  { label: 'Florida', to: '/ai-agency-florida' },
  // ─── collapsed below ───
  { label: 'New York', to: '/ai-agency-new-york' },
  { label: 'Georgia', to: '/ai-agency-georgia' },
  { label: 'Virginia', to: '/ai-agency-virginia' },
  { label: 'Pennsylvania', to: '/ai-agency-pennsylvania' },
  { label: 'Illinois', to: '/ai-agency-illinois' },
  { label: 'Massachusetts', to: '/ai-agency-massachusetts' },
  { label: 'Washington', to: '/ai-agency-washington' },
  { label: 'Arizona', to: '/ai-agency-arizona' },
  { label: 'Colorado', to: '/ai-agency-colorado' },
  { label: 'Tennessee', to: '/ai-agency-tennessee' },
];

// Service Areas = the NC metros where in-person is available (HQ + the
// Lake Norman / Charlotte corridor). HQ first, then the corridor.
const SERVICE_AREAS: FooterLink[] = [
  { label: 'Mooresville, NC', to: '/ai-agency-mooresville-nc' },
  { label: 'Lake Norman, NC', to: '/ai-agency-lake-norman' },
  { label: 'Davidson, NC', to: '/ai-agency-davidson-nc' },
  { label: 'Cornelius, NC', to: '/ai-agency-cornelius-nc' },
  { label: 'Huntersville, NC', to: '/ai-agency-huntersville-nc' },
  // ─── collapsed below ───
  { label: 'Charlotte, NC', to: '/ai-agency-charlotte-nc' },
  // Service×geo pages below, grouped by service in the SAME order as the
  // home-page Services section: App Development → AI Agents → Web Design →
  // Operations Consulting. Collapsed (SEO-safe: still in the DOM + sitemap).
  // App Development.
  { label: 'App Dev · Mooresville', to: '/app-development-mooresville-nc' },
  { label: 'App Dev · Lake Norman', to: '/app-development-lake-norman' },
  { label: 'App Dev · Charlotte', to: '/app-development-charlotte-nc' },
  { label: 'App Dev · Huntersville', to: '/app-development-huntersville-nc' },
  { label: 'App Dev · Cornelius', to: '/app-development-cornelius-nc' },
  { label: 'App Dev · Davidson', to: '/app-development-davidson-nc' },
  // AI Agents (Intelligent Agents).
  { label: 'AI Agents · Mooresville', to: '/intelligent-agents-mooresville-nc' },
  { label: 'AI Agents · Lake Norman', to: '/intelligent-agents-lake-norman' },
  { label: 'AI Agents · Charlotte', to: '/intelligent-agents-charlotte-nc' },
  { label: 'AI Agents · Huntersville', to: '/intelligent-agents-huntersville-nc' },
  { label: 'AI Agents · Cornelius', to: '/intelligent-agents-cornelius-nc' },
  { label: 'AI Agents · Davidson', to: '/intelligent-agents-davidson-nc' },
  // Web Design.
  { label: 'Web Design · Mooresville', to: '/web-design-mooresville-nc' },
  { label: 'Web Design · Lake Norman', to: '/web-design-lake-norman' },
  { label: 'Web Design · Charlotte', to: '/web-design-charlotte-nc' },
  { label: 'Web Design · Huntersville', to: '/web-design-huntersville-nc' },
  { label: 'Web Design · Cornelius', to: '/web-design-cornelius-nc' },
  { label: 'Web Design · Davidson', to: '/web-design-davidson-nc' },
  // Operations Consulting.
  { label: 'Ops Consulting · Mooresville', to: '/operations-consulting-mooresville-nc' },
  { label: 'Ops Consulting · Lake Norman', to: '/operations-consulting-lake-norman' },
  { label: 'Ops Consulting · Charlotte', to: '/operations-consulting-charlotte-nc' },
  { label: 'Ops Consulting · Huntersville', to: '/operations-consulting-huntersville-nc' },
  { label: 'Ops Consulting · Cornelius', to: '/operations-consulting-cornelius-nc' },
  { label: 'Ops Consulting · Davidson', to: '/operations-consulting-davidson-nc' },
];

const LEGAL: FooterLink[] = [
  { label: 'Privacy Policy', to: '/privacy' },
  { label: 'Terms of Service', to: '/terms' },
  { label: 'Cookies', to: '/cookies' },
  { label: 'Cookie Preferences', to: '/preferences' },
  { label: 'Unsubscribe', to: '/unsubscribe' },
];

// External socials shown as icons in the footer socials row. These
// links carry rel="me noopener noreferrer" — the rel="me" portion
// declares cross-platform identity ownership, which AEO crawlers
// (Perplexity, Claude, ChatGPT) and IndieAuth-style verifiers use
// to confirm BRANDCENTRAL is the same entity across platforms.
const FOOTER_SOCIALS = [
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/brandctl/',
    Icon: Linkedin,
  },
  {
    label: 'X',
    href: 'https://x.com/SamKern1234BC',
    Icon: XLogo,
  },
  {
    label: 'GitHub',
    href: 'https://github.com/SamAtBRANDCENTRAL',
    Icon: Github,
  },
  {
    label: 'Google Maps',
    href: 'https://www.google.com/maps/place/BRANDCENTRAL/@35.2578254,-80.860378,9z/data=!3m1!4b1!4m6!3m5!1s0x65c21dab8e52b7b:0x2f74f55814623196!8m2!3d35.2578254!4d-80.860378!16s%2Fg%2F11njxg9p2s',
    Icon: MapPin,
  },
];

interface ColumnProps {
  heading: string;
  links: FooterLink[];
}

/**
 * FooterColumn renders all links in the DOM (crawl-safe) and applies
 * the HTML `hidden` attribute to overflow items past VISIBLE_PER_COLUMN
 * when collapsed. The toggle button uses `aria-expanded` so screen
 * readers announce state. When expanded, hidden items animate in via
 * CSS (no JS animation library required).
 */
const FooterColumn = ({ heading, links }: ColumnProps) => {
  const [expanded, setExpanded] = useState(false);
  const hasOverflow = links.length > VISIBLE_PER_COLUMN;
  const headingId = `footer-col-${heading.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <div>
      {/* Column heading — text-white/65 hits WCAG AA contrast (4.51:1) on
          the #000 footer background. The previous text-white/40 (2.43:1)
          failed Lighthouse's contrast audit. */}
      <p
        id={headingId}
        className="font-sans font-bold tracking-[0.22em] text-white/65 text-[10px] uppercase mb-4"
      >
        {heading}
      </p>
      <ul className="space-y-2.5" aria-labelledby={headingId}>
        {links.map(({ label, to }, i) => {
          const isOverflow = i >= VISIBLE_PER_COLUMN;
          const isCurrentlyHidden = isOverflow && !expanded;
          return (
            <li
              key={to}
              // The HTML `hidden` attribute removes the item from the
              // accessibility tree + layout but keeps it in the DOM —
              // exactly what we want for crawlers (still indexed) and
              // users (visually cleaner footer).
              hidden={isCurrentlyHidden}
            >
              <Link
                to={to}
                className="font-sans text-[12px] md:text-[13px] text-white/75 hover:text-white transition-colors leading-snug"
                tabIndex={isCurrentlyHidden ? -1 : undefined}
              >
                {label}
              </Link>
            </li>
          );
        })}
      </ul>
      {hasOverflow && (
        // Show-all toggle — bumped from text-white/45 (failing contrast)
        // to text-white/70 (passing WCAG AA on dark footer background).
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          aria-controls={headingId}
          className="mt-3 font-sans font-bold tracking-[0.18em] text-[10px] uppercase text-white/70 hover:text-white transition-colors"
        >
          {expanded
            ? '− Show fewer'
            : `+ Show all ${links.length}`}
        </button>
      )}
    </div>
  );
};

export const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <footer className="bc-footer relative z-10 bg-[#101112] border-t border-white/10 pt-5 md:pt-6 pb-8 md:pb-10 px-4 md:px-8">
      {/* 1. Newsletter signup — Field Notes capture. Same AIOS form
          as every other newsletter form on the site. */}
      <FooterNewsletter />

      {/* 2. Nav grid — collapses to 2 cols on mobile, 3 on md, 7 on lg.
          Seven columns at 1250px max-width with gap-10 gives ~150px per
          column. "States" split from "Service Areas" once the P11 state
          pages pushed the combined list past the ~9-item cap. */}
      <div className="w-full max-w-[1250px] mx-auto py-8 md:py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-8 md:gap-10 lg:gap-x-5 lg:gap-y-10">
          <FooterColumn heading="Services" links={SERVICES} />
          <FooterColumn heading="Industries" links={INDUSTRIES} />
          <FooterColumn heading="Compare" links={COMPARE} />
          <FooterColumn heading="Company" links={COMPANY} />
          <FooterColumn heading="Learn" links={LEARN} />
          <FooterColumn heading="States" links={STATES} />
          <FooterColumn heading="Service Areas" links={SERVICE_AREAS} />
          <FooterColumn heading="Legal" links={LEGAL} />
        </div>
      </div>

      {/* 3. Socials row — visible cross-platform identity signals.
          Pill-style icon buttons with rel="me" so AEO crawlers
          (Perplexity, ChatGPT, Claude) and IndieAuth verifiers
          confirm BRANDCENTRAL is the same entity across platforms.
          The same URLs also live in the Organization + LocalBusiness
          sameAs arrays in index.html — visible UI here +
          machine-readable schema there. */}
      <div className="w-full max-w-[1250px] mx-auto pb-6 md:pb-8">
        <ul className="flex flex-wrap items-center justify-center md:justify-start gap-3">
          {FOOTER_SOCIALS.map((s) => (
            <li key={s.label}>
              <a
                href={s.href}
                target="_blank"
                rel="me noopener noreferrer"
                aria-label={`BRANDCENTRAL on ${s.label}`}
                className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/15 hover:border-white/35 bg-white/[0.02] hover:bg-white/[0.05] text-white/65 hover:text-white transition-colors"
              >
                <s.Icon size={16} strokeWidth={1.5} />
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* 4. Bottom row — brand mark (→ home hero) on the left, 15-min
          booking CTA dead-center, copyright on the right. A 3-col grid
          (not flex justify-between) so the CTA is truly centered in the
          footer regardless of how wide the left/right items are. Stacks
          on mobile. */}
      <div className="w-full max-w-[1250px] mx-auto pt-6 md:pt-8 border-t border-white/10 flex flex-col md:grid md:grid-cols-3 items-center gap-4 md:gap-6">
        <button
          type="button"
          onClick={() => goHome(location.pathname, navigate)}
          className="bc-footer-brand flex items-center gap-3 group bg-transparent border-none p-0 cursor-pointer md:justify-self-start"
          aria-label="BRANDCENTRAL: back to top"
        >
          {/* Command-key monogram, on-dark variant. .bc-key-top depresses on
              :active so it presses like the nav logo. */}
          <span className="w-8 h-8 shrink-0 inline-flex items-center justify-center" aria-hidden="true">
            <svg viewBox="0 0 64 64" width="32" height="32" fill="none">
              <rect x="8" y="9" width="48" height="48" rx="14" fill="#2c2c31" stroke="#f4f4f5" strokeWidth="3" />
              <g className="bc-key-top">
                <rect x="8" y="9" width="48" height="42" rx="14" fill="#17181b" stroke="#f4f4f5" strokeWidth="3" />
                <rect x="14" y="15" width="36" height="30" rx="8" fill="none" stroke="#f4f4f5" strokeWidth="1.4" opacity="0.3" />
                <text x="32" y="30.5" fontFamily='"Hanken Grotesk", system-ui, sans-serif' fontSize="19" fontWeight="700" fill="#f4f4f5" textAnchor="middle" dominantBaseline="central" letterSpacing="-1.2">BC</text>
              </g>
            </svg>
          </span>
          <span className="font-sans font-bold tracking-widest text-[11px] text-white/80 group-hover:text-white transition-colors">
            BRANDCENTRAL
          </span>
        </button>

        <a
          href={CONSULTATION_15MIN_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-sans font-bold text-[11px] uppercase tracking-widest text-white hover:text-white border border-white/30 hover:border-white/60 rounded-full px-4 py-2 transition-colors md:justify-self-center"
        >
          Book 15-min call
        </a>

        <p className="font-sans text-[11px] text-white/55 tracking-widest uppercase md:justify-self-end">
          © 2026 BRANDCENTRAL, LLC · Mooresville, NC
        </p>
      </div>
    </footer>
  );
};
